'use client';

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitch } from './LanguageSwitch';
import { SignupProgress } from './SignupProgress';
import { SIGNUP_HEADER_HEIGHT_VAR } from './signup-chrome-style';

/**
 * v8 site header: wordmark, language switch and the step progress bar. The
 * wordmark is not a link: leaving mid-flow would drop the visitor's progress.
 * A page that exists in English only leaves the language switch out.
 * Its height is published on the root so a phone, where the header sticks to
 * the top, scrolls a jumped-to section or a focused field clear of it.
 */
export function SignupHeader({ languageSwitch = true }: { languageSwitch?: boolean }) {
  const { t } = useTranslation('signup');
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(
        SIGNUP_HEADER_HEIGHT_VAR,
        `${Math.ceil(header.getBoundingClientRect().height)}px`,
      );
    publish();
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    resize?.observe(header);
    return () => {
      resize?.disconnect();
      root.style.removeProperty(SIGNUP_HEADER_HEIGHT_VAR);
    };
  }, []);
  return (
    <header ref={headerRef} className="signup-header" data-testid="signup-header">
      <div className="signup-header__bar">
        <img
          className="signup-header__logo"
          src="/brand/pelago-wordmark.svg"
          alt={t('header.logoAlt')}
          data-testid="signup-header-logo"
        />
        {languageSwitch ? <LanguageSwitch /> : null}
      </div>
      <div className="signup-header__progress" data-testid="signup-header-progress">
        <SignupProgress />
      </div>
    </header>
  );
}
